import {
  CalendarDaysIcon,
  ChevronDown,
  ChevronTop,
  ClipboardListIcon,
  CrossIcon,
  EyeIcon,
  Mail,
  MissionIcon,
  NegativeThumb,
  NoteIcon,
  PersonIcon,
  PhoneCallIcon,
  PositiveThumb,
} from 'assets/icons';
import { dayLabel, hourLabel, shortDayLabel, isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import useText from 'hooks/useText';
import { useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

// The icon of an exchange, in its colour.
const EXCHANGE_ICONS = {
  call: [PhoneCallIcon, 'text-emerald-600'],
  email: [Mail, 'text-blue-600'],
  event: [CalendarDaysIcon, 'text-yellow-500'],
  task: [ClipboardListIcon, 'text-gray-700'],
  note: [NoteIcon, 'text-yellow-500'],
};
const CLIENT_ICONS = {
  mission_created: MissionIcon,
  mission_canceled: CrossIcon,
  contract_signed: NoteIcon,
  timesheet_validated: CalendarDaysIcon,
  worker_review: PositiveThumb,
  member_added: PersonIcon,
};
const clientIcon = (item) =>
  item.eventKind === 'worker_review' && item.details?.feedback === 'negative' ? NegativeThumb : CLIENT_ICONS[item.eventKind];

// The line that joins the steps of the history, under the icon.
const Rail = () => <span className="absolute bottom-0 left-4 top-9 w-px bg-gray-100" />;

const Toggle = ({ open, onToggle, label }) => {
  const { text } = useText('recordActivity');
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      className="mt-2 flex items-center gap-1 text-xs font-medium text-gray-900 hover:underline"
    >
      {label ?? text(open ? 'less' : 'more')}
      {open ? <ChevronTop wh={14} color="currentColor" /> : <ChevronDown wh={14} color="currentColor" />}
    </button>
  );
};

// « Lu 3 fois · dernière lecture ven. 9 oct. à 14 h 03 », or « Pas encore lu ».
const readLabel = (opens, text) => {
  if (!opens.length) return text('unread');
  const last = opens[opens.length - 1];
  return text(opens.length === 1 ? 'readOnce' : 'readMany', {
    count: opens.length,
    day: dayLabel(last).toLowerCase(),
    time: hourLabel(last),
  });
};

// « Annuler l'envoi » of a programmed e-mail, until it leaves.
const CancelSend = ({ cancel }) => {
  const { text } = useText('recordActivity');
  const { call } = useCrmContext();
  const notify = useNotifyRecordChanged();
  const [busy, setBusy] = useState(false);
  const run = async (event) => {
    event.stopPropagation();
    setBusy(true);
    try {
      await call(crmApi.cancelRecordEmail, cancel);
      toast.success(text('sendCanceled'));
      notify();
    } catch (error) {
      toast.error(error.message);
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      disabled={busy}
      onClick={run}
      className="mt-2 text-xs font-medium text-gray-900 hover:underline disabled:opacity-50"
    >
      {text('cancelSend')}
    </button>
  );
};

/**
 * An exchange of the history (a call, an e-mail, an appointment, a task, a
 * note): a white card on the line, its title and when, then its text, two
 * lines of it until « Voir plus ». A sent e-mail says how often it was read,
 * a programmed one can be canceled.
 */
const ExchangeEntry = ({ item, onOpen }) => {
  const { text } = useText('recordActivity');
  const [open, setOpen] = useState(false);
  const [Icon, tone] = EXCHANGE_ICONS[item.kind];
  const opens = item.ref && onOpen ? () => onOpen(item.ref) : undefined;
  const long = (item.body?.length ?? 0) > 150 || Boolean(item.header);
  return (
    <div className="relative flex gap-4 pb-3">
      <Rail />
      <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white ${tone}`}>
        <Icon wh={16} color="currentColor" />
      </span>
      <div
        role={opens ? 'button' : undefined}
        tabIndex={opens ? 0 : undefined}
        onClick={opens}
        onKeyDown={opens ? (event) => event.key === 'Enter' && event.target === event.currentTarget && opens() : undefined}
        className={`min-w-0 flex-1 rounded-xl border border-gray-100 bg-white px-4 py-3 ${
          opens ? 'cursor-pointer transition-colors hover:border-gray-200 hover:bg-gray-50' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm font-medium text-gray-900">{item.title}</p>
          <p className="shrink-0 text-xs text-gray-600">
            {item.when ?? `${shortDayLabel(isoDay(item.at))} · ${hourLabel(item.at)}`}
          </p>
        </div>
        {item.from && <p className="mt-0.5 text-xs font-medium text-gray-700">{item.from}</p>}
        {item.opens && (
          <p className={`mt-0.5 flex items-center gap-1.5 text-xs ${item.opens.length ? 'font-medium text-gray-700' : 'text-gray-600'}`}>
            <EyeIcon wh={14} /> {readLabel(item.opens, text)}
          </p>
        )}
        {item.subject && <p className="mt-1 text-sm text-gray-700">{text('subject', { subject: item.subject })}</p>}
        {open && item.header && <p className="text-sm text-gray-700">{item.header}</p>}
        {item.body && (
          <p
            className={`mt-1 whitespace-pre-line text-sm leading-[21px] text-gray-700 ${
              open ? (item.header ? 'mt-4' : '') : 'line-clamp-2'
            }`}
          >
            {item.body}
          </p>
        )}
        {long && <Toggle open={open} onToggle={() => setOpen((current) => !current)} />}
        {item.cancel && <CancelSend cancel={item.cancel} />}
      </div>
    </div>
  );
};

/**
 * What the client did in Jobbiz: a white card like the exchanges, in the
 * name of the person. Several of a kind on a day unfold.
 */
const ClientEntry = ({ item, onOpen }) => {
  const { text } = useText('recordActivity');
  const [open, setOpen] = useState(false);
  const Icon = clientIcon(item);
  const many = item.bundle?.length > 1;
  const actors = many ? [...new Set(item.bundle.filter((one) => !one.self).map((one) => one.actor))] : [];
  const who = (one) =>
    one.self ? (
      one.action.charAt(0).toUpperCase() + one.action.slice(1)
    ) : (
      <>
        <span className="font-medium text-gray-900">{one.actor}</span> {one.action}
      </>
    );
  const opens = !many && item.ref && onOpen ? () => onOpen(item.ref) : undefined;
  return (
    <div className="relative flex gap-4 pb-3">
      <Rail />
      <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-700">
        <Icon wh={16} color="currentColor" />
      </span>
      <div
        role={opens ? 'button' : undefined}
        tabIndex={opens ? 0 : undefined}
        onClick={opens}
        onKeyDown={opens ? (event) => event.key === 'Enter' && event.target === event.currentTarget && opens() : undefined}
        className={`min-w-0 flex-1 rounded-xl border border-gray-100 bg-white px-4 py-3 ${
          opens ? 'cursor-pointer transition-colors hover:border-gray-200 hover:bg-gray-50' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <p className="min-w-0 text-sm text-gray-700">
            {many ? (
              <>
                <span className="font-medium text-gray-900">{text(`clientMany.${item.eventKind}`, { count: item.bundle.length })}</span>
                {actors.length > 0 &&
                  ` ${text('clientBy', { who: actors.length > 1 ? text('people', { count: actors.length }) : actors[0] })}`}
              </>
            ) : (
              who(item)
            )}
          </p>
          <p className="shrink-0 text-xs text-gray-600">{`${shortDayLabel(isoDay(item.at))} · ${hourLabel(item.at)}`}</p>
        </div>
        {item.from && <p className="mt-0.5 text-xs font-medium text-gray-700">{item.from}</p>}
        {many && open && (
          <div className="mt-2 flex flex-col">
            {item.bundle.map((one) => (
              <button
                key={one.key}
                type="button"
                onClick={() => onOpen?.(one.ref)}
                className="-mx-2 flex items-start justify-between gap-4 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                <span className="min-w-0">{who(one)}</span>
                <span className="shrink-0 text-xs text-gray-600">{hourLabel(one.at)}</span>
              </button>
            ))}
          </div>
        )}
        {many && (
          <Toggle
            open={open}
            onToggle={() => setOpen((current) => !current)}
            label={open ? text('less') : text('seeAll', { count: item.bundle.length })}
          />
        )}
      </div>
    </div>
  );
};

// A step of the history: an exchange, or an action of the client.
const ActivityEntry = ({ item, onOpen }) =>
  item.kind === 'client' ? <ClientEntry item={item} onOpen={onOpen} /> : <ExchangeEntry item={item} onOpen={onOpen} />;

export default ActivityEntry;
